import { Clock, ClipboardCheck, ShoppingCart, Moon, type LucideIcon } from 'lucide-react';
import type { TimeOfDay } from '@/hooks/useTimeOfDay';

export type TimeStateTone = 'sunrise' | 'lime' | 'sunset' | 'night';

export interface TimeStateConfig {
  /** Visual tone (drives accent color of eyebrow dot, CTA, hero background). */
  tone: TimeStateTone;
  /** i18n key for the greeting line. */
  greetingKey: string;
  /** Emoji or icon glyph rendered next to greeting (e.g. "☀", "🌅", "🌙"). */
  greetingGlyph: string;
  /** i18n key for the eyebrow label (kicker above the hero). */
  eyebrowKey: string;
  /** i18n key for the primary CTA label. */
  ctaLabelKey: string;
  /** Lucide icon for the primary CTA. */
  ctaIcon: LucideIcon;
  /** Route the CTA should navigate to (or '#' if handled programmatically). */
  ctaHref: string;
  /** i18n key for the footer hint shown under the CTA cluster. */
  footerHintKey: string;
  /** Whether to show the critical alert banner in this state. */
  showAlertBanner: boolean;
  /** Hero font size in px — slightly smaller at night to fit weekly totals. */
  heroFontSizePx: number;
  /** Mobile FAB label (kept short, mono uppercase). */
  fabMobileKey: string;
  /** Desktop FAB label. */
  fabDesktopKey: string;
}

export const TIME_STATE_CONFIG: Record<TimeOfDay, TimeStateConfig> = {
  morning: {
    tone: 'sunrise',
    greetingKey: 'dashboard:greeting_morning',
    greetingGlyph: '☀',
    eyebrowKey: 'dashboard:eyebrow_morning',
    ctaLabelKey: 'dashboard:cta_open_register',
    ctaIcon: Clock,
    ctaHref: '/pos',
    footerHintKey: 'dashboard:hint_morning',
    showAlertBanner: true,
    heroFontSizePx: 92,
    fabMobileKey: 'dashboard:fab_mobile_morning',
    fabDesktopKey: 'dashboard:fab_desktop_morning',
  },
  day: {
    tone: 'lime',
    greetingKey: 'dashboard:greeting_day',
    greetingGlyph: '☀',
    eyebrowKey: 'dashboard:eyebrow_day',
    ctaLabelKey: 'dashboard:cta_start_sale',
    ctaIcon: ShoppingCart,
    ctaHref: '/pos',
    footerHintKey: 'dashboard:hint_day',
    showAlertBanner: true,
    heroFontSizePx: 92,
    fabMobileKey: 'dashboard:fab_mobile_day',
    fabDesktopKey: 'dashboard:fab_desktop_day',
  },
  evening: {
    tone: 'sunset',
    greetingKey: 'dashboard:greeting_evening',
    greetingGlyph: '🌅',
    eyebrowKey: 'dashboard:eyebrow_evening',
    ctaLabelKey: 'dashboard:cta_close_day',
    ctaIcon: ClipboardCheck,
    ctaHref: '/finances',
    footerHintKey: 'dashboard:hint_evening',
    showAlertBanner: true,
    heroFontSizePx: 92,
    fabMobileKey: 'dashboard:fab_mobile_evening',
    fabDesktopKey: 'dashboard:fab_desktop_evening',
  },
  night: {
    tone: 'night',
    greetingKey: 'dashboard:greeting_night',
    greetingGlyph: '🌙',
    eyebrowKey: 'dashboard:eyebrow_night',
    ctaLabelKey: 'dashboard:cta_prepare_tomorrow',
    ctaIcon: Moon,
    ctaHref: '/finances',
    footerHintKey: 'dashboard:hint_night',
    showAlertBanner: false,
    heroFontSizePx: 76,
    fabMobileKey: 'dashboard:fab_mobile_night',
    fabDesktopKey: 'dashboard:fab_desktop_night',
  },
};

/** Tailwind v4 classes for the per-tone CTA primary button. */
export const TONE_CTA_CLASSES: Record<TimeStateTone, string> = {
  sunrise:
    'bg-amber-500 text-white shadow-[0_8px_24px_-8px_rgba(245,158,11,0.55)] hover:bg-amber-600',
  lime: 'bg-lime-400 text-charcoal-950 shadow-[0_8px_24px_-8px_rgba(163,230,53,0.5)] hover:bg-lime-300',
  sunset:
    'bg-orange-500 text-white shadow-[0_8px_24px_-8px_rgba(249,115,22,0.55)] hover:bg-orange-600',
  night:
    'bg-white text-charcoal-950 border border-slate-200 shadow-sm hover:bg-slate-50 dark:bg-charcoal-800 dark:text-slate-100 dark:border-charcoal-700',
};

/** Tailwind v4 classes for the eyebrow dot accent. */
export const TONE_DOT_CLASSES: Record<TimeStateTone, string> = {
  sunrise: 'bg-amber-500 shadow-[0_0_8px_currentColor] text-amber-500',
  lime: 'bg-lime-400 shadow-[0_0_8px_currentColor] text-lime-400',
  sunset: 'bg-orange-500 shadow-[0_0_8px_currentColor] text-orange-500',
  night: 'bg-violet-500 shadow-[0_0_8px_currentColor] text-violet-500',
};

/** Background radial gradient class for the main area (subtle ambient). */
export const TONE_BG_CLASSES: Record<TimeStateTone, string> = {
  sunrise:
    'bg-[radial-gradient(ellipse_80%_60%_at_70%_20%,rgba(245,158,11,0.08)_0%,transparent_70%)]',
  lime: 'bg-[radial-gradient(ellipse_80%_70%_at_50%_30%,rgba(163,230,53,0.07)_0%,transparent_70%)]',
  sunset:
    'bg-[radial-gradient(ellipse_90%_70%_at_80%_80%,rgba(249,115,22,0.08)_0%,transparent_70%)]',
  night: 'bg-[radial-gradient(ellipse_90%_70%_at_30%_10%,rgba(139,92,246,0.1)_0%,transparent_70%)]',
};
